import { ref, readonly, type Ref } from 'vue';

import { getMediaDevices, listenForDeviceChanges } from '@bv/utils/common/device';

/**
 * @type {Ref<MediaDeviceInfo[]>}
 * @private
 * A shared, reactive reference to the list of media devices.
 */
const devices: Ref<MediaDeviceInfo[]> = ref<MediaDeviceInfo[]>([]);

/**
 * @type {Ref<boolean>}
 * @private
 * A shared, reactive reference indicating if a microphone is available.
 */
const hasMic: Ref<boolean> = ref(false);

/**
 * @type {Ref<boolean>}
 * @private
 * A shared, reactive reference indicating if a speaker is available.
 */
const hasSpeakers: Ref<boolean> = ref(false);

/**
 * @type {Ref<boolean>}
 * @private
 * A shared, reactive reference indicating if a camera is available.
 */
const hasCamera: Ref<boolean> = ref(false);

/**
 * @type {boolean}
 * @private
 * A flag to ensure the device listeners are only initialized once.
 */
let isInitialized: boolean = false;

/**
 * A self-contained, store-less composable for managing and monitoring media devices.
 * It initializes listeners on its first run and provides shared, reactive state
 * that is updated automatically when devices change.
 *
 * @returns {{
 * devices: Readonly<Ref<readonly MediaDeviceInfo[]>>,
 * hasMic: Readonly<Ref<boolean>>,
 * hasSpeakers: Readonly<Ref<boolean>>,
 * hasCamera: Readonly<Ref<boolean>>
 * }} An object containing readonly refs to the device state.
 */
export function useDevices(): {
  devices: Readonly<Ref<readonly MediaDeviceInfo[]>>;
  hasMic: Readonly<Ref<boolean>>;
  hasSpeakers: Readonly<Ref<boolean>>;
  hasCamera: Readonly<Ref<boolean>>;
} {
  if (!isInitialized) {
    /**
     * Updates the shared reactive state based on a new device list.
     * @param {MediaDeviceInfo[]} deviceList - The new list of devices.
     */
    const updateState = (deviceList: MediaDeviceInfo[]) => {
      devices.value = deviceList;
      hasMic.value = deviceList.some((d) => d.kind === 'audioinput');
      hasSpeakers.value = deviceList.some((d) => d.kind === 'audiooutput');
      hasCamera.value = deviceList.some((d) => d.kind === 'videoinput');
    };

    // Get the initial list and set up the listener for changes.
    getMediaDevices(true).then(updateState);
    listenForDeviceChanges(updateState);

    isInitialized = true;
  }

  // Return readonly versions of the state to prevent direct mutation from components.
  return {
    devices: readonly(devices),
    hasMic: readonly(hasMic),
    hasSpeakers: readonly(hasSpeakers),
    hasCamera: readonly(hasCamera),
  };
}
